import { apiClient } from "./client";

export interface ChannelType {
  id: string;
  name: string;
  icon: string;
  category: string;
  authType: "oauth2" | "api_key";
  supportedFeatures: string[];
  pseudoChannelEnabled?: boolean;
  isActive?: boolean;
}

/**
 * A pseudo-channel descriptor returned by GET /channels/user/:id/pseudo-channels.
 * Each entry mirrors a top-level data entity (e.g., a Meta ad account) of the
 * underlying real channel.
 */
export interface PseudoChannelCandidate {
  sourceEntityId: string;
  name: string;
  externalId: string;
  status?: string | null;
  parentChannelId: string;
}

export interface Channel {
  id: string;
  channelType: string;
  name: string;
  status: "active" | "error" | "pending" | "syncing" | "inactive" | "disconnected" | "uninstalled";
  lastSyncAt: string | null;
  lastReloadedAt?: string | null;
  createdAt: string;
  icon?: string; // Shop's icon for Shopify, or custom icon for other channels
  channelTypeConfig?: {
    name: string;
    icon: string;
    category: string;
    supportedFeatures?: string[];
    pseudoChannelEnabled?: boolean;
  };
  syncDetails?: {
    commercialMessage?: string;
    commercialAction?: string;
    isRetryable?: boolean;
    syncStatus?: string;
    errorCode?: number;
  } | null;
  shopDomain?: string | null;
}

export interface SyncJob {
  id: string;
  status: "running" | "completed" | "failed" | "retrying";
  startedAt: string;
  completedAt?: string;
  operationsProcessed?: number;
  errorMessage?: string;
  metadata?: string;
}

export interface SyncStatus {
  channel: {
    id: string;
    status: string;
    lastSyncAt: string | null;
  };
  recentSyncJobs: SyncJob[];
  entitiesCount: number;
  entities: Array<{
    id: string;
    entityType: string;
    name: string;
    syncStatus: string;
  }>;
}

export interface OAuthInitiateResponse {
  authUrl: string;
  state: string;
}

export const channelsService = {
  // Get all available channel types
  getChannelTypes: async (): Promise<{ success: boolean; data: ChannelType[] }> => {
    const response = await apiClient.get("/channels");
    return response.data;
  },

  // Get user's channels (previously called connections)
  getUserChannels: async (): Promise<{ success: boolean; data: Channel[]; journals?: any[] }> => {
    const response = await apiClient.get("/channels/user");
    return response.data;
  },

  // Get specific user channel
  getUserChannel: async (channelId: string): Promise<{ success: boolean; data: Channel }> => {
    const response = await apiClient.get(`/channels/user/${channelId}`);
    return response.data;
  },

  // Initiate OAuth flow
  initiateOAuth: async (
    channelId: string,
    params?: Record<string, any>
  ): Promise<{ success: boolean; data: OAuthInitiateResponse }> => {
    const response = await apiClient.post("/oauth/initiate", {
      channelId,
      params,
    });
    return response.data;
  },

  // Delete channel
  deleteChannel: async (channelId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/channels/user/${channelId}`);
    return response.data;
  },

  // Get entities for a channel
  getEntities: async (channelId: string): Promise<{ success: boolean; data: any[] }> => {
    const response = await apiClient.get(`/channels/user/${channelId}/entities`);
    return response.data;
  },

  // Retry entity sync for a channel
  retryEntitySync: async (channelId: string): Promise<{ success: boolean; data: { message: string; syncJob: SyncJob } }> => {
    const response = await apiClient.post(`/channels/user/${channelId}/retry-sync`);
    return response.data;
  },

  // Get sync status for a channel
  getSyncStatus: async (channelId: string): Promise<{ success: boolean; data: SyncStatus }> => {
    const response = await apiClient.get(`/channels/user/${channelId}/sync-status`);
    return response.data;
  },

  // Test channel
  testChannel: async (channelId: string): Promise<{ success: boolean; data: { isValid: boolean; status: string } }> => {
    const response = await apiClient.post(`/channels/user/${channelId}/test`);
    return response.data;
  },

  // Connect channel directly (e.g. WooCommerce)
  connectChannel: async (
    channelType: string,
    credentials: any,
    metadata?: any
  ): Promise<{ success: boolean; channelId?: string; error?: string }> => {
    const response = await apiClient.post("/channels/connect", {
      channelType,
      credentials,
      metadata,
    });
    return response.data;
  },

  // Reload channel data
  reloadChannel: async (channelId: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    const response = await apiClient.post(`/channels/user/${channelId}/reload`);
    return response.data;
  },

  // List pseudo-channel candidates for a real channel (e.g., Meta ad accounts).
  // Empty array if the channel does not support pseudo-channels.
  getPseudoChannels: async (
    channelId: string
  ): Promise<{ success: boolean; data: PseudoChannelCandidate[] }> => {
    const response = await apiClient.get(`/channels/user/${channelId}/pseudo-channels`);
    return response.data;
  },
};
